'use client';

import * as React from 'react';

type InvertFillButtonVariant = 'blue' | 'white' | 'red';
type InvertFillButtonSize = 'sm' | 'md' | 'lg';
type InvertFillButtonFontSize = 'sm' | 'base' | 'lg' | 'xl';
type InvertFillButtonFontWeight = 'sm' | 'medium' | 'semibold' | 'bold';

type BaseProps = {
  variant?: InvertFillButtonVariant;
  size?: InvertFillButtonSize;
  fontSize?: InvertFillButtonFontSize;
  fontWeight?: InvertFillButtonFontWeight;
  className?: string;
  children: React.ReactNode;
};

type ButtonProps = BaseProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    href?: never;
  };

type AnchorProps = BaseProps &
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    href: string;
  };

type InvertFillButtonProps = ButtonProps | AnchorProps;

const styles = `
  .invert-fill-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border-radius: 8px;
    border: 2px solid rgba(59, 130, 246, 0.4);
    padding: 12px 32px;
    color: #2563eb;
    transition: all 300ms;
    cursor: pointer;
    text-decoration: none;
    background: transparent;
  }

  .invert-fill-btn .slider {
    position: absolute;
    inset: 0;
    z-index: 0;
    transform: translateY(101%);
    transition: transform 300ms;
  }

  .invert-fill-btn .content {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .invert-fill-btn .content svg {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
  }

  .invert-fill-btn .content span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  /* Variant: Blue */
  .invert-fill-btn.variant-blue {
    border: 2px solid #004195;
    background-color: #004195;
    color: white;
  }
  .invert-fill-btn.variant-blue .slider {
    background-color: white;
  }
  .invert-fill-btn.variant-blue:hover {
    color: #004195;
  }

  /* Variant: White */
  .invert-fill-btn.variant-white {
    border: 2px solid #004195;
    background-color: white;
    color: #004195;
  }
  .invert-fill-btn.variant-white .slider {
    background-color: #004195;
  }
  .invert-fill-btn.variant-white:hover {
    color: white;
  }

  /* Variant: Red */
  .invert-fill-btn.variant-red {
    border: 2px solid #dc2626;
    background-color: #dc2626;
    color: white;
  }
  .invert-fill-btn.variant-red .slider {
    background-color: white;
  }
  .invert-fill-btn.variant-red:hover {
    border-color: #dc2626;
    color: #dc2626;
  }

  /* Sizes */
  .invert-fill-btn.size-sm {
    height: 36px;
    padding: 0 20px;
  }
  .invert-fill-btn.size-md {
    height: 40px;
    padding: 0 24px;
  }
  .invert-fill-btn.size-lg {
    height: 44px;
    padding: 0 28px;
  }

  /* Font Sizes */
  .invert-fill-btn.font-size-sm {
    font-size: 13px;
  }
  .invert-fill-btn.font-size-base {
    font-size: 14px;
  }
  .invert-fill-btn.font-size-lg {
    font-size: 16px;
  }
  .invert-fill-btn.font-size-xl {
    font-size: 18px;
  }

  /* Font Weights */
  .invert-fill-btn.font-weight-sm {
    font-weight: 300;
  }
  .invert-fill-btn.font-weight-medium {
    font-weight: 500;
  }
  .invert-fill-btn.font-weight-semibold {
    font-weight: 600;
  }
  .invert-fill-btn.font-weight-bold {
    font-weight: 700;
  }

  /* Hover effects */
  .invert-fill-btn:hover {
    border-color: #3b82f6;
    box-shadow: 0 0 20px -5px rgba(59, 130, 246, 0.4);
    transform: scale(0.98);
  }

  .invert-fill-btn:hover .slider {
    transform: translateY(0%);
  }
`;

export function InvertFillButton(props: InvertFillButtonProps) {
  const {
    variant = 'blue',
    size = 'md',
    fontSize = 'base',
    fontWeight = 'medium',
    className,
    children,
    ...restProps
  } = props;

  const classNames = [
    'invert-fill-btn',
    `variant-${variant}`,
    `size-${size}`,
    `font-size-${fontSize}`,
    `font-weight-${fontWeight}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const buttonContent = (
    <>
      <div className="slider" />
      <span className="content">{children}</span>
    </>
  );

  if ('href' in props && props.href) {
    return (
      <>
        <style>{styles}</style>
        <a className={classNames} {...(restProps as AnchorProps)}>
          {buttonContent}
        </a>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <button type="button" className={classNames} {...(restProps as ButtonProps)}>
        {buttonContent}
      </button>
    </>
  );
}
